import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * The homepage used to issue six separate single-row `admin_settings` reads
 * (logo, version, staging flag, GA id, featured-in toggle, how-it-works block).
 * They are all tiny, public and cached the same way, so one batched request
 * serves them and keeps the first paint cheap.
 */
export const ADMIN_SETTINGS_BATCH_KEY = ["admin-settings-batch"] as const;

export const BATCHED_SETTING_KEYS = [
  "podgo_version",
  "branding_logo_url",
  "branding_logo_dark_url",
  "staging_mode",
  "google_analytics_id",
  "featured_in_enabled",
  "how_it_works_enabled",
  "how_it_works_eyebrow",
  "how_it_works_heading",
  "how_it_works_subheading",
  "how_it_works_video_url",
  "how_it_works_poster_url",
  "gtm_container_id",
  "gtm_enabled",
  "auth_provider_status",
  "email_style",
] as const;


export type BatchedSettingKey = (typeof BATCHED_SETTING_KEYS)[number];
export type SettingsMap = Partial<Record<BatchedSettingKey, string>>;

async function fetchBatchedSettings(): Promise<SettingsMap> {
  const { data, error } = await supabase
    .from("admin_settings")
    .select("key, value")
    .in("key", BATCHED_SETTING_KEYS as unknown as string[]);
  if (error) throw error;
  const map: SettingsMap = {};
  data?.forEach((row) => {
    map[row.key as BatchedSettingKey] = row.value ?? "";
  });
  return map;
}

/** Shared, cached read of the batched settings. Optionally projected. */
export function useAdminSettingsBatch<T = SettingsMap>(
  select?: (map: SettingsMap) => T,
  options?: Pick<UseQueryOptions<SettingsMap, Error, T>, "staleTime">,
) {
  return useQuery<SettingsMap, Error, T>({
    queryKey: ADMIN_SETTINGS_BATCH_KEY,
    queryFn: fetchBatchedSettings,
    select,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
    retry: 1,
  });
}

/** Single value out of the batched read, with a fallback. */
export function useAdminSetting(key: BatchedSettingKey, fallback = "") {
  return useAdminSettingsBatch<string>((map) => map[key] ?? fallback);
}
